import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { SplitText } from 'gsap/SplitText';
import { cubicBezier } from '../utils/easing';

gsap.registerPlugin(SplitText);

export default function initTexts() {
  // Elements inside .panel are animated by panel.js on open — skip them here
  // so we don't register ScrollTriggers against off-screen / fixed panels.
  const notInPanel = (el) => !el.closest('.panel');

  // Fade-in images on load. This only toggles a CSS class (the fade itself is
  // a CSS transition that the reduced-motion media query can soften), and it's
  // what makes images visible at all — so it must run regardless of motion.
  document.querySelectorAll('.fade-img').forEach((img) => {
    if (img.complete) {
      img.classList.add('is-loaded');
    } else {
      img.addEventListener('load', () => img.classList.add('is-loaded'));
    }
  });

  const mm = gsap.matchMedia();

  mm.add({
    motion: '(prefers-reduced-motion: no-preference)',
    reduce: '(prefers-reduced-motion: reduce)',
  }, (ctx) => {
    const { reduce } = ctx.conditions;

    if (reduce) {
      // Skip every reveal tween. Targets that prime their hidden state in JS
      // are simply never touched (so they render in their natural state); the
      // ones whose hidden resting state lives in CSS must be forced visible
      // here, or they'd stay invisible.

      // About background items rest at opacity:0 + transform in CSS.
      const bgItems = document.querySelectorAll('.about__background__items .item');
      if (bgItems.length) gsap.set(bgItems, { opacity: 1, x: 0, y: 0, scale: 1 });

      // Subtitle pills rest at scale:0 / opacity:0 in CSS.
      Array.from(document.querySelectorAll('.subtitle'))
        .filter(notInPanel)
        .forEach((el) => {
          const bg = el.querySelector('.subtitle__bg');
          const text = el.querySelector('.subtitle__text');
          if (bg) gsap.set(bg, { scale: 1 });
          if (text) gsap.set(text, { opacity: 1 });
        });

      return;
    }

    // Titles — word by word reveal
    // (word splits reflow naturally on resize, no special handling needed)
    const titles = Array.from(document.querySelectorAll('.title')).filter(notInPanel);

    titles.forEach((el) => {
      const split = SplitText.create(el, {
        type: 'words',
        wordsClass: 'word',
        mask: 'words',
        aria: false,
      });

      gsap.set(split.words, {
        yPercent: 120,
        rotateY: -20,
        willChange: 'transform',
        transformOrigin: '50% 100%',
      });

      gsap.to(split.words, {
        yPercent: 0,
        rotateY: 0,
        duration: 1.2,
        stagger: 0.1,
        ease: 'power3.out',
        force3D: true,
        scrollTrigger: {
          trigger: el,
          start: 'top 85%',
        },
        onComplete() {
          gsap.set(split.words, { willChange: 'auto' });
        },
      });
    });

    // Rich-text paragraphs & text-anim — paragraph-by-paragraph reveal.
    // Each element's content is wrapped in an inner span (the animated target);
    // the element itself acts as the mask via overflow:hidden in CSS.
    const textElements = Array.from(document.querySelectorAll('.rich-text p, .text-anim')).filter(notInPanel);

    // Group consecutive siblings so adjacent paragraphs share a stagger.
    const groups = [];
    textElements.forEach((el) => {
      const lastGroup = groups[groups.length - 1];
      if (lastGroup && lastGroup[lastGroup.length - 1].nextElementSibling === el) {
        lastGroup.push(el);
      } else {
        groups.push([el]);
      }
    });

    groups.forEach((group) => {
      const targets = group.map((el) => {
        let inner = el.querySelector(':scope > .paragraph__inner');
        if (!inner) {
          inner = document.createElement('span');
          inner.className = 'paragraph__inner';
          while (el.firstChild) inner.appendChild(el.firstChild);
          el.appendChild(inner);
        }
        return inner;
      });

      gsap.set(targets, {
        y: (i, target) => parseFloat(getComputedStyle(target).lineHeight) || 40,
        rotateY: -8,
        opacity: 0,
        transformPerspective: 600,
        willChange: 'transform, opacity',
        transformOrigin: '50% 100%',
      });

      gsap.to(targets, {
        y: 0,
        rotateY: 0,
        opacity: 1,
        duration: 1.8,
        stagger: 0.15,
        ease: 'power3.out',
        force3D: true,
        scrollTrigger: {
          trigger: group[0],
          start: 'top 85%',
        },
        onComplete() {
          gsap.set(targets, { willChange: 'auto' });
        },
      });
    });


    // Stagger-reveal list links & document links
    const staggerGroups = document.querySelectorAll('.list, .documents');

    staggerGroups.forEach((container) => {
      const items = container.querySelectorAll(':scope > a, :scope > div');
      if (!items.length) return;

      gsap.set(items, {
        yPercent: 100,
        opacity: 0,
      });

      gsap.to(items, {
        yPercent: 0,
        opacity: 1,
        duration: 1.2,
        stagger: 0.1,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: container,
          start: 'top 85%',
        },
      });
    });

    // About background items — logo-style reveal
    const bgItemsContainer = document.querySelector('.about__background__items');
    if (bgItemsContainer) {
      const items = bgItemsContainer.querySelectorAll('.item');

      items.forEach((item, i) => {
        if (i >= 2) item.classList.add('from-right');
        if (i === 0 || i === items.length - 1) item.classList.add('from-further');
      });

      gsap.to(items, {
        opacity: 1,
        x: 0,
        y: 0,
        scale: 1,
        duration: 1.2,
        ease: cubicBezier(0.22, -0.01, 0.13, 0.99),
        stagger: 0.07,
        scrollTrigger: {
          trigger: bgItemsContainer,
          start: 'top 85%',
        },
      });
    }

    // Subtitles — pill reveal
    const subtitles = Array.from(document.querySelectorAll('.subtitle')).filter(notInPanel);

    subtitles.forEach((el) => {
      const bg = el.querySelector('.subtitle__bg');
      const text = el.querySelector('.subtitle__text');
      if (!bg || !text) return;

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: el,
          start: 'top 85%',
        },
      });

      // 1. Background pill scales in
      tl.to(bg, {
        scale: 1,
        duration: 0.6,
        ease: 'power3.out',
      });

      // 2. Text fades in (slight overlap)
      tl.to(text, {
        opacity: 1,
        duration: 0.4,
        ease: 'power2.out',
      }, '-=0.2');
    });
  });
}
